/**
 * User groups: manager/member teams.
 * - userGroups: { name, managerId, joinCode, createdAt }
 * - userGroupMembers: { groupId, userId, status: 'pending'|'approved', email?, requestedAt, approvedAt? }
 * Manager can see member runs by adding managerId to run.visibleToManagerUids when member is approved.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const USER_GROUPS = 'userGroups';
const USER_GROUP_MEMBERS = 'userGroupMembers';

function generateJoinCode(length = 6) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  const bytes = new Uint8Array(length);
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
    for (let i = 0; i < length; i++) code += chars[bytes[i] % chars.length];
  } else {
    for (let i = 0; i < length; i++) code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

/**
 * Create a new group; caller becomes manager.
 * @param {string} name - Group name
 * @param {string} managerId - Firebase Auth UID of the manager
 * @returns {Promise<{ id: string, joinCode: string }>}
 */
export async function createGroup(name, managerId) {
  const joinCode = generateJoinCode(6);
  const ref = await addDoc(collection(db, USER_GROUPS), {
    name: (name || '').trim() || 'My group',
    managerId,
    joinCode,
    createdAt: serverTimestamp(),
  });
  return { id: ref.id, joinCode };
}

/**
 * List groups where user is manager.
 */
export async function getGroupsManagedBy(uid) {
  const q = query(
    collection(db, USER_GROUPS),
    where('managerId', '==', uid)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * List groups where user is an approved member.
 */
export async function getGroupsWhereMember(uid) {
  const q = query(
    collection(db, USER_GROUP_MEMBERS),
    where('userId', '==', uid),
    where('status', '==', 'approved')
  );
  const snap = await getDocs(q);
  const groups = [];
  for (const d of snap.docs) {
    const data = d.data();
    const groupSnap = await getDoc(doc(db, USER_GROUPS, data.groupId));
    if (groupSnap.exists()) {
      groups.push({ id: groupSnap.id, ...groupSnap.data(), memberDocId: d.id });
    }
  }
  return groups;
}

/**
 * Get group by join code (for "join group" flow).
 */
export async function getGroupByJoinCode(joinCode) {
  const normalized = (joinCode || '').trim().toUpperCase();
  if (!normalized) return null;
  const q = query(
    collection(db, USER_GROUPS),
    where('joinCode', '==', normalized)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Request to join a group (creates pending membership).
 */
export async function requestToJoinGroup(groupId, userId, email) {
  const existing = query(
    collection(db, USER_GROUP_MEMBERS),
    where('groupId', '==', groupId),
    where('userId', '==', userId)
  );
  const existingSnap = await getDocs(existing);
  if (!existingSnap.empty) {
    const status = existingSnap.docs[0].data().status;
    if (status === 'approved') return { already: true, approved: true };
    return { already: true, approved: false };
  }
  await addDoc(collection(db, USER_GROUP_MEMBERS), {
    groupId,
    userId,
    email: email || null,
    status: 'pending',
    requestedAt: serverTimestamp(),
  });
  return { requested: true };
}

/**
 * List members of a group (for manager view).
 */
export async function getGroupMembers(groupId) {
  const q = query(
    collection(db, USER_GROUP_MEMBERS),
    where('groupId', '==', groupId)
  );
  const snap = await getDocs(q);
  const members = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const userIds = [...new Set(members.map((m) => m.userId))];
  const users = {};
  for (const uid of userIds) {
    try {
      const u = await getDoc(doc(db, 'users', uid));
      if (u.exists()) users[uid] = u.data();
    } catch (_) {}
  }
  return members.map((m) => ({
    ...m,
    username: users[m.userId]?.username || users[m.userId]?.email || m.userId,
    email: users[m.userId]?.email || m.email,
  }));
}

/**
 * Approve a pending member. A Cloud Function (onUserGroupMemberChange) updates
 * all the member's runs to add managerId to visibleToManagerUids.
 */
export async function approveMember(memberDocId, groupId, managerId) {
  const memberRef = doc(db, USER_GROUP_MEMBERS, memberDocId);
  const memberSnap = await getDoc(memberRef);
  if (!memberSnap.exists()) throw new Error('Member not found');
  const data = memberSnap.data();
  if (data.status === 'approved') return;
  const groupSnap = await getDoc(doc(db, USER_GROUPS, groupId));
  if (!groupSnap.exists() || groupSnap.data().managerId !== managerId) {
    throw new Error('Not the group manager');
  }
  await updateDoc(memberRef, {
    status: 'approved',
    approvedAt: serverTimestamp(),
  });
}

/**
 * Remove a member. A Cloud Function (onUserGroupMemberChange) removes
 * managerId from all the member's runs' visibleToManagerUids.
 */
export async function removeMember(memberDocId, groupId, managerId) {
  const memberRef = doc(db, USER_GROUP_MEMBERS, memberDocId);
  const memberSnap = await getDoc(memberRef);
  if (!memberSnap.exists()) throw new Error('Member not found');
  const groupSnap = await getDoc(doc(db, USER_GROUPS, groupId));
  if (!groupSnap.exists() || groupSnap.data().managerId !== managerId) {
    throw new Error('Not the group manager');
  }
  await deleteDoc(memberRef);
}

/**
 * Get all user IDs whose runs the current user can view: self + approved members of groups they manage.
 */
export async function getViewableRunUserIds(uid) {
  const managed = await getGroupsManagedBy(uid);
  const memberIds = new Set([uid]);
  for (const group of managed) {
    const members = await getGroupMembers(group.id);
    members
      .filter((m) => m.status === 'approved')
      .forEach((m) => memberIds.add(m.userId));
  }
  return Array.from(memberIds);
}
